/**
 * t331u B5+: phone workspace settings. `/settings/<group>/<item>` for an item in the shared PHONE_WS_SETTINGS shows its
 * phone page here (Me lists them); the rest of Settings keeps the "best on a computer" card. Same API and checks as
 * the desktop panels; the server stays the authority on permissions.
 */
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronRight, Plus } from 'lucide-react';
import { canUsePhoneWs, SETTINGS_PALETTE, type PhoneWsSetting } from '@tradeoutlet/shared';
import { usePermissions } from '@/lib/permissions';
import { Spinner } from '@/components/ui';
import { BackHeader } from '../buildings/common';
import { ShiftPresetsPage } from './ShiftPresets';
import { ClosuresPage } from './Closures';
import { ChecklistSchedulesPage } from './ChecklistSchedules';
import { ChecklistTemplatesPage } from '../ChecklistTemplates';
import { ghostBtn, saveBtn } from './PersonSettings';
import { PositionsCataloguePage } from './PositionsCatalogue';
import { TimeRulesPage } from './TimeRules';
import { RotaPolicyPage } from './RotaPolicy';
import { ClockInPage } from './ClockIn';
import { CelebrationsPage, KudosBadgesPage } from './Engagement';
import { QrPointsPage } from './QrPoints';
import { EquipmentCategoriesPage } from './EquipmentCategories';
import { InspectionTypesPage } from './InspectionTypes';
import { HrExpiringPage } from './PersonHrDocs';
import { HrChangesPage, HrMissingPage, HrReportsPage } from './HrOps';
import { DeletionRequestsPanel, ReportsPanel } from '@/features/moderation/panels';

export function PhoneWorkspaceSettings({ setting }: { setting: PhoneWsSetting }) {
  const mine = usePermissions();
  const ok = canUsePhoneWs(mine.data, setting);
  return (
    <div className="flex h-full flex-col" data-testid={`m-ws-${setting.item}`}>
      <BackHeader fallback="/" title={setting.title} />
      {mine.isLoading ? <div className="p-6"><Spinner /></div>
        : !ok ? <p className="p-4 text-sm text-slate-600 dark:text-slate-300" data-testid="m-ws-denied">You can’t change {setting.title.toLowerCase()} here.</p>
        : setting.item === 'presets' ? <ShiftPresetsPage />
        : setting.item === 'closures' ? <ClosuresPage />
        : setting.item === 'checklist-templates' ? <ChecklistTemplatesPage />
        : setting.item === 'checklist-schedules' ? <ChecklistSchedulesPage />
        : setting.item === 'positions' ? <PositionsCataloguePage />
        : setting.item === 'time-rules' ? <TimeRulesPage />
        : setting.item === 'policy' ? <RotaPolicyPage />
        : setting.item === 'clock-in' ? <ClockInPage />
        : setting.item === 'kudos' ? <KudosBadgesPage />
        : setting.item === 'celebrations' ? <CelebrationsPage />
        : setting.item === 'qr-points' ? <QrPointsPage />
        : setting.item === 'equipment-categories' ? <EquipmentCategoriesPage />
        : setting.item === 'equipment-inspection-types' ? <InspectionTypesPage />
        : setting.item === 'hr-expiring' ? <HrExpiringPage />
        : setting.item === 'hr-changes' ? <HrChangesPage />
        : setting.item === 'hr-reports' ? <HrReportsPage />
        : setting.item === 'hr-missing' ? <HrMissingPage />
        : setting.item === 'reports' ? <div className="min-h-0 flex-1 overflow-y-auto p-4"><ReportsPanel /></div>
        : setting.item === 'deletion-requests' ? <div className="min-h-0 flex-1 overflow-y-auto p-4"><DeletionRequestsPanel /></div>
        : null}
    </div>
  );
}

/** A tappable list row: optional colour bar, title, second line and a chevron. */
export function WsRow({ title, line, color, onClick, right, testId }: { title: ReactNode; line?: ReactNode; color?: string | null; onClick?: () => void; right?: ReactNode; testId?: string }) {
  return (
    <div className="flex min-h-[56px] items-center border-b border-slate-100 dark:border-slate-800" data-testid={testId}>
      <button type="button" onClick={onClick} disabled={!onClick} className="flex min-h-[56px] min-w-0 flex-1 items-center gap-3 px-3 text-left active:bg-slate-100 dark:active:bg-slate-800">
        {color !== undefined && <span className="h-8 w-1.5 shrink-0 rounded-full" style={{ background: color ?? '#94a3b8' }} aria-hidden />}
        <span className="min-w-0 flex-1"><span className="block truncate text-base">{title}</span>{line && <span className="block text-sm text-slate-500">{line}</span>}</span>
        {onClick && !right && <ChevronRight className="h-5 w-5 shrink-0 text-slate-400" aria-hidden />}
      </button>
      {right}
    </div>
  );
}
export function WsAddRow({ label, onClick, testId }: { label: string; onClick: () => void; testId: string }) {
  return <button type="button" onClick={onClick} data-testid={testId} className="flex min-h-[48px] w-full items-center gap-3 px-3 text-left text-base font-medium active:bg-slate-100 dark:active:bg-slate-800"><Plus className="h-5 w-5" aria-hidden />{label}</button>;
}
export const listCard = 'mx-3 overflow-hidden rounded-2xl border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900';
export const note = 'px-4 pt-3 text-sm text-slate-500';

/** The shared palette as 44px swatches; the picked one is ringed in black. */
export function ColourSwatches({ value, onChange, testId, palette = SETTINGS_PALETTE }: { value: string; onChange: (c: string) => void; testId: string; palette?: readonly string[] }) {
  const list = palette.includes(value) ? palette : [...palette, value];
  return (
    <div className="flex flex-wrap gap-1" role="radiogroup" aria-label="Colour" data-testid={testId}>
      {list.map(c => (
        <button key={c} type="button" role="radio" aria-checked={value === c} aria-label={`Colour ${c}`} onClick={() => onChange(c)} className="flex h-11 w-11 items-center justify-center">
          <span className={clsx('h-8 w-8 rounded-full ring-1 ring-black/10', value === c && 'ring-[3px] ring-black dark:ring-white')} style={{ background: c }} />
        </button>
      ))}
    </div>
  );
}

/** A full-width on/off row (48px): label on the left, the brand switch on the right. */
export function WsSwitch({ label, checked, onChange, disabled, testId }: { label: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; testId?: string }) {
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} data-testid={testId}
      className="flex min-h-[48px] w-full items-center gap-3 py-1 text-left text-base disabled:opacity-50">
      <span className="min-w-0 flex-1">{label}</span>
      <span className={clsx('relative h-7 w-12 shrink-0 rounded-full transition', checked ? 'bg-accent ring-1 ring-inset ring-ink' : 'bg-slate-300 dark:bg-slate-700')} aria-hidden>
        <span className={clsx('absolute top-1 h-5 w-5 rounded-full shadow transition', checked ? 'left-6 bg-ink' : 'left-1 bg-white')} />
      </span>
    </button>
  );
}
/** A small uppercase heading above a list card. */
export const WsHead = ({ children }: { children: ReactNode }) => <h2 className="mb-1 mt-4 px-4 text-xs font-semibold uppercase tracking-wide text-slate-500">{children}</h2>;

/** Undo + Save pinned under a settings form; problems (if any) show above it. */
export function WsSaveBar({ dirty, busy, problems = [], onUndo, onSave, testId }: { dirty: boolean; busy: boolean; problems?: string[]; onUndo: () => void; onSave: () => void; testId: string }) {
  return (
    <div className="shrink-0 border-t border-slate-200 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-950" data-testid={`${testId}-savebar`}>
      {dirty && problems.length > 0 && <p className="pb-2 text-sm text-rose-700 dark:text-rose-300" role="alert" data-testid={`${testId}-problem`}>{problems[0]}</p>}
      <div className="flex gap-2">
        <button type="button" className={ghostBtn} disabled={!dirty || busy} onClick={onUndo} data-testid={`${testId}-undo`}>Undo</button>
        <button type="button" className={saveBtn} disabled={!dirty || busy || problems.length > 0} onClick={onSave} data-testid={`${testId}-save`}>Save</button>
      </div>
    </div>
  );
}
